import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pie } from 'react-chartjs-2';
import BllinkUtils from '../../../common/utils/utils';

const paymentMethods = {
    cheques: {color: '#85C2FF'},
    wires: {color: '#6E74FF'},
    cash: {color: '#9C88FB'},
    creditCard: {color: '#a8c0ff'},
    bankCharge: {color: '#61D7FF'},
    autoCheque: {color: '#05566d'},
    tenantRecord: {color: '#85C2FF'},
    legacyPayment: {color: '#57a1eb'},
    notSet: {color: '#5680de'}
}

const js = BllinkUtils.JsExtras;
const Money = BllinkUtils.Money;
const Strings = BllinkUtils.Strings;

export const LineGraph = ({ expenses, incomes, showExpenses, showIncomes, ltr }) => {
    const { t } = useTranslation();
    const monthlyAmountsForExpenses = expenses.map(data => Money.roundNumber(data.amount.value / 100, 0));
    const monthlyAmountsForIncomes = incomes.map(data => Money.roundNumber(data.amount.value / 100, 0));
    const monthNames = expenses.map(data => t(`common.months.${data.month}`));

    const [series, setSeries] = useState([
        { 
            data: monthlyAmountsForExpenses,
            name: t('buildings.expenses') 
        }
    ]);
    const [colors, setColors] = useState(['#E532BE']);

    useEffect(() => {
        if (showIncomes && showExpenses) {
            setSeries([
                { 
                    data: monthlyAmountsForExpenses, 
                    name: t('buildings.expenses') 
                },
                { 
                    data: monthlyAmountsForIncomes,
                    name: t('buildings.incomes') 
                }
            ]);
            setColors(['#E532BE', '#585DDB']);
        } else if (showIncomes) {
            setSeries([
                { 
                    data: monthlyAmountsForIncomes,
                    name: t('buildings.incomes') 
                }
            ]);
            setColors(['#585DDB']);
        } else if (showExpenses) {
            setSeries([
                { 
                    data: monthlyAmountsForExpenses, 
                    name: t('buildings.expenses') 
                }
            ]); 
            setColors(['#E532BE']);
        }
    }, [showExpenses, showIncomes, expenses, incomes]);

    const options = {
        chart: {
            type: 'line',
            zoom: { enabled: false},
            background: "#fff"
        },
        colors: colors,
        stroke: {
            width: 4,
            curve: 'smooth'
        },
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories: monthNames
        },
        legend: {
            show: false
        },
        markers: {
            size: 0
        },
        noData:{
            text: 'No Data',
            align: 'center',
            verticalAlign: 'top'
        },
        grid: {
            padding: {
                left: ltr ? 20 : 50
            },
        }
    };

    const [ApexChart, setApexChart] = useState({ Component: null });

    useEffect(async () => {
        if (ApexChart.Component == null) {
            const module = await import('react-apexcharts');
            setApexChart({ Component: module.default });
        }
    });

    return ( 
        ApexChart.Component == null ? '' :
        <ApexChart.Component
            options={options}
            series={series}
            type='area'
            height={200}
        />
    )
}

export const PieChart = ({ pieChartData }) => {
    const { t } = useTranslation();

    const getMethodLabel = (paymentMethodData, totalPaidByAllMethods) => {
        const methodName = t(`buildings.${paymentMethodData.name}`);
        if (!paymentMethodData.totalPaid) return;
        const percentageOfPayments = Money.roundNumber((paymentMethodData.totalPaid/totalPaidByAllMethods) * 100, 0);
        return `${percentageOfPayments}% ${methodName}`;
    };

    let borderColor = '#FFFFFF';

    const totalPaidByAllMethods = js.sum(pieChartData, 'totalPaid');
    let labels = pieChartData.map(pm => getMethodLabel(pm, totalPaidByAllMethods));
    let amounts = pieChartData.map(pm => pm.totalPaid);
    let colors = pieChartData.map(pm => paymentMethods[pm.name].color);

    if (labels.length === 0) {
        labels = [t('buildings.no_payments_made_this_month_yet')];
        amounts = [1];
        colors = ['#a8c0ff'];
        borderColor = 'black';
    }

    let oilData = {
        labels: labels,
        datasets: [{
            data: amounts,
            backgroundColor: colors,
            borderColor: borderColor,
            borderWidth: 2,
        }]
    };

    return (
        <Pie
            data={oilData}
            options={{
                responsive: true,
                maintainAspectRatio: true,
                font: {
                    size: 12,
                    family: 'Arimo, sans-serif', // FIXME: use styles and global css-variables
                },
                plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                        displayColors: false,
                        
                    }
                }
            }}
        />
    )
}
